'use client';

import { Icons } from '@/components/icons';
import { Badge } from '@/components/ui/badge';
import { Kbd, KbdGroup } from '@/components/ui/kbd';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { FEATURE_FLAGS } from '@/hooks/use-feature-flag/constants';
import useIsMac from '@/hooks/use-is-mac';
import dynamic from 'next/dynamic';
import { Fragment } from 'react';
import { displayKeys, SPINE } from './keyboard/shortcuts';
import { openGlobalSearch } from './open-event';

const GlobalSearch = dynamic(() =>
  import('@/lib/global-search').then((m) => m.GlobalSearch)
);
const GlobalSearchV2 = dynamic(() => import('.').then((m) => m.GlobalSearchV2));

/**
 * Flag-selected ⌘K palette. The new search and the legacy `lib/global-search`
 * modal coexist with **only one mounted at a time** (PRD: feature-flagged
 * coexistence), so `⌘K` and the global hotkeys bind to exactly one
 * implementation. Mounted once, globally, in the protected branch of
 * `app/providers.tsx`; visible entry points render `GlobalSearchTrigger`
 * instead of a second palette. Retiring the old search + deleting the flag is
 * a separate follow-up once the new search is validated.
 */
export function GlobalSearchSwitch() {
  const useV2 = useFeatureFlag(FEATURE_FLAGS.GLOBAL_SEARCH_V2);

  if (useV2) {
    return <GlobalSearchV2 />;
  }

  return <GlobalSearch />;
}

/** Visible search-input button. Opens the globally mounted palette. */
export function GlobalSearchTrigger({
  showBetaBadge,
}: {
  showBetaBadge?: boolean;
}) {
  const isMac = useIsMac();
  return (
    <button
      type='button'
      onClick={() => openGlobalSearch({ page: 'search' })}
      className='flex h-10 w-full cursor-text items-center justify-start rounded-md border border-input bg-surface py-2 pr-2 pl-3 text-sm text-muted-foreground'
    >
      <Icons.search className='mr-2 size-4' />
      Sök i systemet
      {showBetaBadge ? (
        <Badge variant='yellow' className='ml-auto'>
          Beta
        </Badge>
      ) : (
        <KbdGroup className='ml-auto'>
          {displayKeys(SPINE.togglePalette, isMac).map((key, i) => (
            <Fragment key={key}>
              {i > 0 && '+'}
              <Kbd>{key}</Kbd>
            </Fragment>
          ))}
        </KbdGroup>
      )}
    </button>
  );
}
